import * as THREE from 'three';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config';

/**
 * 草图倒角几何参数解析器。
 *
 * 职责：
 * 1. 按第一选择实体、第二选择实体和方向角解析角度—距离倒角；
 * 2. 校验方向角是否位于当前角点的可行域；
 * 3. 返回第二实体上的缩进距离，不修改任何草图对象。
 */
export class SketchChamferGeometryResolver implements SketchChamferGeometryResolverLike {
    /**
     * 解析角度—距离倒角在第二实体上的缩进距离。
     *
     * 几何语义：
     * 1. `distance1` 沿第一选择实体从角点量取；
     * 2. `directionAngleDeg` 是从第一实体朝第二实体测量的倒角方向角；
     * 3. 由角点、两个倒角端点组成的三角形使用正弦定理求第二缩进距离。
     *
     * @param cornerAngle 两条源实体从角点指向保留侧的夹角，单位为弧度。
     * @param distance1 第一选择实体上的缩进距离。
     * @param directionAngleDeg 从第一实体朝第二实体测量的方向角，单位为度。
     * @returns 第二选择实体上的缩进距离；参数不在可行域时返回 null。
     */
    public resolveSecondTrimDistance(
        cornerAngle: number,
        distance1: number,
        directionAngleDeg: number,
    ): number | null {
        const denominatorTolerance = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trigonometricDenominator;
        const directionAngle = THREE.MathUtils.degToRad(directionAngleDeg);
        const secondEndpointAngle = Math.PI - cornerAngle - directionAngle;

        /**
         * 方向角与第二端点角必须都严格位于三角形内部。
         * 禁止使用绝对值把越过第二实体的无效角度翻回到另一条几何分支。
         */
        if (
            !Number.isFinite(cornerAngle)
            || !Number.isFinite(distance1)
            || !Number.isFinite(directionAngle)
            || cornerAngle <= denominatorTolerance
            || cornerAngle >= Math.PI - denominatorTolerance
            || distance1 <= denominatorTolerance
            || directionAngle <= denominatorTolerance
            || secondEndpointAngle <= denominatorTolerance
        ) {
            return null;
        }

        const denominator = Math.sin(secondEndpointAngle);
        if (Math.abs(denominator) <= denominatorTolerance) {
            return null;
        }

        const distance2 = distance1 * Math.sin(directionAngle) / denominator;
        return Number.isFinite(distance2) && distance2 > denominatorTolerance
            ? distance2
            : null;
    }
}
